// "열 설정" 다이얼로그 — 보임/숨김 체크와 ↑/↓ 순서 바꾸기. 드래그 정렬은 MUI X Pro 기능이라
// (이 프로젝트는 Community 라이선스) 쓰지 않고, 키보드로도 되는 버튼 방식으로 대신한다.
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemIcon,
  ListItemText, Checkbox, IconButton, Button, Stack, Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { isUserToggleable } from './gridPref';

/**
 * 전체 칼럼(순서대로) 중 사용자가 만질 수 있는 것만 뽑아, 체크/순서 조작 대상 목록을 만든다.
 * 체크박스·액션 칼럼처럼 코드가 넣은 특수 칼럼과, 코드가 애초에 숨긴 칼럼은 여기 안 보인다.
 */
const toggleableFieldsOf = (columns, codeVisibilityModel) =>
  (columns || []).filter((c) => isUserToggleable(c, codeVisibilityModel)).map((c) => c.field);

/** 사용자가 바꾼 "만질 수 있는 칼럼 순서"를 전체 칼럼 순서에 되돌려 끼워 넣는다.
 * 특수/숨김 칼럼은 원래 있던 자리(코드 순서)를 그대로 지킨다. */
const reorderFull = (columns, toggleableFields, draftOrder) => {
  const toggleableSet = new Set(toggleableFields);
  let i = 0;
  return (columns || []).map((c) => {
    if (!toggleableSet.has(c.field)) return c.field;
    const next = draftOrder[i];
    i += 1;
    return next;
  });
};

const headerOf = (columns, field) => columns.find((c) => c.field === field)?.headerName || field;

const GridColumnSettingsDialog = ({ open, onClose, columns, codeVisibilityModel, hidden, onApply, onReset }) => {
  const toggleableFields = toggleableFieldsOf(columns, codeVisibilityModel);
  const [draftOrder, setDraftOrder] = useState(toggleableFields);
  const [draftHidden, setDraftHidden] = useState(new Set(hidden || []));

  // 열 때마다 지금 상태로 다시 맞춘다(취소하고 다시 열었을 때 이전 임시 변경이 남지 않도록).
  useEffect(() => {
    if (open) {
      setDraftOrder(toggleableFieldsOf(columns, codeVisibilityModel));
      setDraftHidden(new Set(hidden || []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const move = (field, dir) => {
    setDraftOrder((prev) => {
      const idx = prev.indexOf(field);
      const nextIdx = idx + dir;
      if (idx < 0 || nextIdx < 0 || nextIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[nextIdx]] = [next[nextIdx], next[idx]];
      return next;
    });
  };

  const toggleHidden = (field) => {
    setDraftHidden((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field); else next.add(field);
      return next;
    });
  };

  const handleApply = () => {
    const order = reorderFull(columns, toggleableFields, draftOrder);
    onApply({ order, hidden: Array.from(draftHidden) });
    onClose();
  };

  const handleReset = () => {
    onReset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>열 설정</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {draftOrder.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 2 }}>조정할 수 있는 열이 없습니다.</Typography>
        ) : (
          <List dense disablePadding>
            {draftOrder.map((field, idx) => (
              <ListItem
                key={field}
                secondaryAction={(
                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      size="small"
                      aria-label={`${headerOf(columns, field)} 위로`}
                      disabled={idx === 0}
                      onClick={() => move(field, -1)}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={`${headerOf(columns, field)} 아래로`}
                      disabled={idx === draftOrder.length - 1}
                      onClick={() => move(field, 1)}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                )}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    edge="start"
                    checked={!draftHidden.has(field)}
                    onChange={() => toggleHidden(field)}
                  />
                </ListItemIcon>
                <ListItemText primary={headerOf(columns, field)} />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button color="inherit" onClick={handleReset}>기본값으로</Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>취소</Button>
          <Button variant="contained" onClick={handleApply}>적용</Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default GridColumnSettingsDialog;
